<template>
  <div>
    <appHeader></appHeader>
    <div class="container-fluid admin-batch-user-create-tool">
      <div class="row">
        <div class="col-md-3 col-sm-12 pr-0 dark-bg">
          <appAdminSidebar></appAdminSidebar>
        </div>
        <transition appear name="fade">
          <div class="col-md-9 p-4">
            <div class="row">
              <div class="col-sm-6 col-md-6">
                <h4>User Batch Creation Manager</h4>
              </div>
              <div class="col-sm-6 col-md-6 align-right">
                <a href="/admin-points-faq" target="_blank"
                  >Frequently Asked Questions</a
                >
              </div>
            </div>
            <p class="my-2">
              This tool will allow you to create large amounts of users with
              just the required information. You will need an
              <a @click="saveTemplate()">Excel template</a> to fill employees'
              username, email, first name, last name and company-id.
            </p>
            <hr />
            <a-spin :spinning="fileUploadStatus === 'saving'">
              <div
                class="col-sm-5 col-md-5 card summary"
                v-if="
                  file &&
                    (fileUploadStatus === 'uploaded' ||
                      fileUploadStatus === 'saving')
                "
              >
                <div class="m-3">
                  <h4>File Upload Results</h4>
                  <hr />
                  <p>
                    File
                    <strong>{{ lastUploads.at(-1).file.name }}</strong> uploaded
                    on <strong>{{ lastUploads.at(-1).uploadTime }}</strong>
                  </p>
                  <hr />
                  <label
                    >{{
                      lastUploads.at(-1).uploadResult.no_change_user_count
                    }}
                    User(s) need no change</label
                  >
                  <hr />
                  <label
                    >{{ lastUploads.at(-1).uploadResult.total_insert_count }} to
                    be created</label
                  >
                  <ul
                    v-if="lastUploads.at(-1).uploadResult.insert_record_details"
                  >
                    <li
                      v-for="user in lastUploads.at(-1).uploadResult
                        .insert_record_details"
                      :key="user.client_id"
                    >
                      {{ user.count }} new user(s) in
                      {{ `${user.client_name} (${user.client_id})` }}
                    </li>
                  </ul>
                  <div
                    v-if="
                      lastUploads.at(-1).uploadResult.users_to_update &&
                      lastUploads.at(-1).uploadResult.users_to_update.length > 0
                        ? true
                        : false
                    "
                  >
                    <label>Users to update</label>
                    <p>
                      The following users currently have different data in the
                      database or are locked. You can review the new data (shown
                      in the following list) and update them now, or leave them
                      untouched for the time being
                    </p>
                    <div>
                      <a-table
                        :columns="usersToUpdateFields"
                        :data-source="usersToUpdate"
                        :pagination="usersToUpdatePagination"
                        :row-key="(record) => record.id"
                      >
                        <span slot="update" slot-scope="text, record">
                          <a-checkbox
                            @change="
                              (e) =>
                                handleChange(
                                  e.target.checked,
                                  record.id,
                                  'update'
                                )
                            "
                            :checked="text"
                            :disabled="!record.editable"
                          />
                        </span>
                        <span slot="locked" slot-scope="text, record">
                          <a-checkbox
                            @change="
                              (e) =>
                                handleChange(
                                  e.target.checked,
                                  record.id,
                                  'is_locked'
                                )
                            "
                            :checked="text"
                            :disabled="!record.editable"
                          />
                        </span>
                        <template
                          v-for="col in ['last_name', 'first_name', 'email']"
                          :slot="col"
                          slot-scope="text, record"
                        >
                          <a-input
                            v-if="record.editable"
                            style="margin: -5px 0"
                            :value="text"
                            @change="
                              (e) =>
                                handleChange(e.target.value, record.id, col)
                            "
                            :key="col"
                          />
                          <span v-else :key="col" :title="text">
                            {{ text }}
                          </span>
                        </template>
                        <template slot="company" slot-scope="text, record">
                          <a-select
                            v-if="record.editable"
                            style="margin: -5px 0"
                            :value="text"
                            :options="
                              authorizedClients.map(
                                ({ clientId, clientName }) => ({
                                  value: clientId.toString(),
                                  label: clientName,
                                })
                              )
                            "
                            @change="
                              (value) =>
                                handleChange(value, record.id, 'client_id')
                            "
                          />
                          <span
                            v-else
                            :title="
                              authorizedClients.find(
                                ({ clientId }) => clientId.toString() === text
                              )
                                ? authorizedClients.find(
                                    ({ clientId }) =>
                                      clientId.toString() === text
                                  ).clientName
                                : text
                            "
                          >
                            {{
                              authorizedClients.find(
                                ({ clientId }) => clientId.toString() === text
                              )
                                ? authorizedClients.find(
                                    ({ clientId }) =>
                                      clientId.toString() === text
                                  ).clientName
                                : text
                            }}
                          </span>
                        </template>
                        <template slot="points" slot-scope="text, record">
                          <a-input
                            v-if="record.editable"
                            style="margin: -5px 0"
                            :value="text"
                            @change="
                              (e) =>
                                handleChange(
                                  Number(e.target.value),
                                  record.id,
                                  'points'
                                )
                            "
                          />
                          <span
                            v-else-if="record.points < 0"
                            class="badge badge-danger"
                            >{{ record.points.toFixed }}</span
                          >
                          <span
                            v-else-if="record.points >= 0"
                            class="badge badge-success"
                            >{{ record.points }}</span
                          >
                        </template>
                        <template slot="operation" slot-scope="text, record">
                          <div class="editable-row-operations">
                            <span v-if="record.editable">
                              <a @click="() => save(record.id)">Save &nbsp;</a>
                              <a-popconfirm
                                title="Sure to cancel?"
                                @confirm="() => cancel(record.id)"
                              >
                                <a>Cancel</a>
                              </a-popconfirm>
                            </span>
                            <span v-else>
                              <a
                                :disabled="editusersToUpdateId !== ''"
                                @click="() => edit(record.id)"
                                >Edit</a
                              >
                            </span>
                          </div>
                        </template>
                      </a-table>
                    </div>
                  </div>
                  <div
                    v-if="
                      lastUploads.at(-1).uploadResult.users_to_be_ignored &&
                      lastUploads.at(-1).uploadResult.users_to_be_ignored
                        .length > 0
                        ? true
                        : false
                    "
                  >
                    <label>Users to be ignored</label>
                    <p>
                      The uploaded file contains incomplete data for the
                      following users. These users will be ignored right now.
                      Please address the listed issues in a future upload
                    </p>
                    <div>
                      <a-table
                        :columns="ignoredUsersFields"
                        :data-source="
                          lastUploads.at(-1).uploadResult.users_to_be_ignored
                        "
                        :pagination="ignoredUsersPagination"
                        :row-key="(record) => record.user_name"
                      >
                      </a-table>
                    </div>
                  </div>
                  <div class="fileUploadResultAction">
                    <button
                      class="btn btn-secondary btn-lg btn-sm-block mr-1 my-2"
                      @click="cancelFileSave()"
                    >
                      Cancel
                    </button>
                    <button
                      class="btn btn-primary btn-lg btn-sm-block mr-1 my-2"
                      @click="saveUserCreationFile()"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </a-spin>
            <div
              class="col-sm-5 col-md-5 card summary"
              v-if="file && fileUploadStatus === 'saved'"
            >
              <div class="m-3">
                <h4>Batch process successful</h4>
                <hr />
                <p>
                  File
                  <strong>{{ lastUploads.at(-1).file.name }}</strong> successful
                  on <strong>{{ lastUploads.at(-1).saveTime }}</strong>
                </p>
                <hr />
                <label
                  >{{ lastUploads.at(-1).saveResult.no_change_count }} User(s)
                  left untouched</label
                >
                <hr />
                <label
                  >{{ lastUploads.at(-1).saveResult.update_count }} User(s)
                  updated</label
                >
                <hr />
                <label
                  >{{ lastUploads.at(-1).saveResult.inserted_count }} User(s)
                  created</label
                >
                <hr />
                <label
                  >{{ lastUploads.at(-1).saveResult.ignored_count }} User(s)
                  couldn't be updated due to errors in the provided data</label
                >
                <hr />
                <div class="fileUploadResultAction">
                  <button
                    class="btn btn-secondary btn-lg btn-sm-block mr-1 my-2"
                    @click="closeFileSave()"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
            <a-spin :spinning="fileUploadStatus === 'uploading'">
              <div class="row my-2">
                <div class="col-sm-7 col-md-7">
                  <label>Upload User-Creation File</label>
                  <p class="my-2">
                    Large files can take up to <strong>10 minutes</strong> to
                    upload and process. Once uploaded, you will get notification
                    of the results in your email inbox. Upload only the
                    <a @click="saveTemplate()">provided Excel template</a>.
                  </p>
                  <div class="card">
                    <div class="p-3">
                      <a-upload
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        :showUploadList="{
                          showPreviewIcon: false,
                          showRemoveIcon: false,
                        }"
                        :before-upload="beforeUpload"
                        :file-list="fileList"
                      >
                        <a-button> Select File </a-button>
                      </a-upload>
                    </div>
                  </div>
                  <button
                    class="btn btn-primary btn-lg btn-sm-block mr-1 my-2"
                    :disabled="!file"
                    @click="showUploadConfirm = true"
                  >
                    {{ fileUploadStatus === "uploading" ? "Processing" : `Upload User-Creation File` }}
                  </button>
                  <hr />
                  <div v-if="previousUpload.data.length > 0">
                    <div class="lastUploadsHeader">
                      <label>Last uploads </label>
                      <a @click="toggleAllUpload()">
                        View all Uploads <font-awesome-icon icon="angle-down" />
                      </a>
                    </div>
                    <a-spin :spinning="previousUpload.loading">
                      <div
                        v-for="upload in previousUpload.data"
                        :key="upload.id"
                        class="uploadResult"
                      >
                        <div
                          class="results-row d-flex"
                          style="align-items: center"
                        >
                          <a-icon
                            type="clock-circle"
                            theme="filled"
                            v-if="upload.is_in_progress"
                          />
                          <a-icon
                            type="check-circle"
                            style="color: rgb(40, 167, 69)"
                            theme="filled"
                            v-else
                          />

                          <div
                            :title="upload.file_name"
                            :style="
                              upload.is_in_progress
                                ? 'color:black;'
                                : 'color: #1890ff; cursor: pointer;'
                            "
                            @click="downloadExcel(upload)"
                          >
                            {{ upload.file_name }}
                          </div>
                          <div>{{ parseInt(upload.file_size / 1000) }} kb</div>
                          <div :title="upload.upload_date">
                            {{
                              moment(upload.upload_date).format(
                                "MM/DD/YYYY hh:mm A"
                              )
                            }}
                          </div>
                          <div>
                            <a v-b-toggle="upload.id.toString()">
                              summary <font-awesome-icon icon="angle-down" />
                            </a>
                          </div>
                        </div>
                        <b-collapse class="card" :id="upload.id.toString()">
                          <div class="p-3">
                            <strong>{{ upload.users_created }}</strong> new
                            users created,
                            <strong>{{ upload.users_untouched }}</strong> users
                            untouched,
                            <strong>{{ upload.users_updated }}</strong> users
                            updated,
                            <strong>{{ upload.users_skipped }}</strong> users
                            skipped.
                          </div>
                        </b-collapse>
                      </div>
                    </a-spin>
                  </div>
                </div>
                <div class="col-sm-5 col-md-5 card summary">
                  <div class="m-3">
                    <label>Get Excel Template</label>
                    <div class="display-flex my-2">
                      <a @click="saveTemplate()">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 384 512"
                          width="14px"
                          height="14px"
                          style="fill: #3782f3"
                        >
                          <path
                            d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm160-14.1v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"
                          />
                        </svg>
                        Download Blank Template
                        <i class="fas fa-file-download"></i>
                      </a>
                    </div>
                    <hr />
                    <div class="display-flex my-2">
                      <a v-b-toggle.collapse-a>
                        <font-awesome-icon icon="angle-down" />
                        Instructions To Fill Template
                      </a>
                    </div>
                    <b-collapse id="collapse-a" class="mt-2">
                      <b-card
                        >Please make sure that the xls spreadsheet you upload
                        follows the provided
                        <a @click="saveTemplate()">template:</a></b-card
                      >
                      <b-card
                        >it must contain
                        <strong>username, email, first_name, last_name</strong>
                        and <strong>client_id columns;</strong> the
                        <strong>transaction_comments</strong> is
                        optional.</b-card
                      >
                      <b-card
                        >If you are unsure of how to use the template, check out
                        the
                        <a href="/admin-points-faq" target="_blank"
                          >Frequently Asked Questions</a
                        ></b-card
                      >
                    </b-collapse>
                    <hr />
                    <div class="my-2">
                      <label>Authorized Companies/Locations</label>
                      <ul style="list-style-type: none">
                        <li
                          v-for="Client in authorizedClients"
                          :key="Client.clientId"
                        >
                          <div class="row">
                            <div class="col-sm-6 col-lg-6 col-md-6">
                              <p>{{ Client.clientName }}</p>
                            </div>
                            <div class="col-sm-6 col-lg-6 col-md-6">
                              <p>ID&nbsp;{{ Client.clientId }}</p>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </a-spin>
          </div>
        </transition>
      </div>
    </div>
    <appFooter></appFooter>
    <a-modal
      :visible="allUpload.visible"
      title="All Uploads"
      width="800px"
      :footer="null"
      @cancel="toggleAllUpload()"
    >
      <a-spin :spinning="allUpload.loading">
        <div
          v-for="upload in allUpload.data"
          :key="upload.id"
          class="uploadResult"
        >
          <div class="results-row d-flex" style="align-items: center">
            <a-icon
              type="clock-circle"
              theme="filled"
              v-if="upload.is_in_progress"
            />
            <a-icon
              type="check-circle"
              style="color: rgb(40, 167, 69)"
              theme="filled"
              v-else
            />

            <div
              :title="upload.file_name"
              :style="
                upload.is_in_progress
                  ? 'color:black;'
                  : 'color: #1890ff; cursor: pointer;'
              "
              @click="downloadExcel(upload)"
            >
              {{ upload.file_name }}
            </div>
            <div>{{ parseInt(upload.file_size / 1000) }} kb</div>
            <div :title="upload.upload_date">
              {{ moment(upload.upload_date).format("MM/DD/YYYY hh:mm A") }}
            </div>
            <div>
              <a v-b-toggle="`a${upload.id}`">
                summary <font-awesome-icon icon="angle-down" />
              </a>
            </div>
          </div>
          <b-collapse class="card" :id="`a${upload.id}`">
            <div class="p-3">
              <strong>{{
                parseInt(upload.users_created).toLocaleString()
              }}</strong>
              new users created,
              <strong>{{
                parseInt(upload.users_untouched).toLocaleString()
              }}</strong>
              users untouched,
              <strong>{{
                parseInt(upload.users_updated).toLocaleString()
              }}</strong>
              users updated,
              <strong>{{
                parseInt(upload.users_skipped).toLocaleString()
              }}</strong>
              users skipped.
            </div>
          </b-collapse>
        </div>
        <a-pagination @change="allUpload.change" v-bind="allUpload" />
      </a-spin>
    </a-modal>
    <a-modal
      v-model="showUploadConfirm"
      title="Confirm Upload"
      width="420px"
      :maskClosable="false"
      ok-text="Upload"
      cancel-text="Cancel"
      @ok="confirmUpload"
    >
      <div class="confirm-modal">
        <h4>Are you sure?</h4>
        <p>A bulk action is about to be performed and will affect multiple users.</p>
        <p>This may take up to <strong>10 minutes</strong>.</p>
        <p>You will receive a notification by email after completion.</p>
      </div>
    </a-modal>

  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import AdminSidebar from "@/views/admin/AdminSidebar.vue";
import { saveAs } from "file-saver";
import moment from "moment";
import { fileDownload } from "@/utilities/downloadFile.js";

export default {
  name: "BatchUserCreateTool",

  props: {
    activeMenuItem: String,
    activeMenu: String,
  },

  components: {
    appHeader: Header,
    appFooter: Footer,
    appAdminSidebar: AdminSidebar,
  },

  methods: {
    setActiveMenuItem: function() {
      this.$store.dispatch("adminSidebar/setActive", {
        activeMenuItem: this.activeMenuItem
          ? this.activeMenuItem
          : "usersBatchUserCreateTool",
        activeMenu: this.activeMenu ? this.activeMenu : "users",
      });
    },
    confirmUpload() {
      this.showUploadConfirm = false;
      this.uploadFile();
    },
    beforeUpload(file) {
      this.file = file;
      return false;
    },
    uploadFile() {
      this.$store
        .dispatch("adminBatchUserCreateTool/uploadUserCreationFile", this.file)
        .then((uploadResult) => {
          if (uploadResult.duplicate_user_names?.length > 0) {
            this.$error({
              title: "Error",
              content: (
                <div>
                  Please remove following duplicate username(s)
                  <ul>
                    {uploadResult.duplicate_user_names?.map(
                      (user) => user?.length > 0 && <li>{user}</li>
                    )}
                  </ul>
                </div>
              ),
            });
          } else if (uploadResult.users_to_update) {
            this.usersToUpdate = uploadResult.users_to_update.map((user) => ({
              ...user,
              update: true,
            }));
            this.usersToUpdateFinalized = uploadResult.users_to_update.map(
              (user) => ({ ...user, update: true })
            );
            this.editusersToUpdateId = "";
          }
          this.getPreviousUpload(1, this.previousUpload.pageSize);
          this.getAllUpload(1, this.allUpload.pageSize);
        }).catch((err) => {
        // HANDLE BACKEND ERRORS HERE
          const msg = err.message || "Something went wrong while uploading file.";
          this.$error({
            title: "Upload Failed",
            content: msg,
          });
          this.$store.dispatch("adminBatchUserCreateTool/updateFileUploadStatus", "");
        });
    },
    saveTemplate() {
      saveAs(new Blob([this.template]), "BatchUserCreate_template.xlsx");
    },
    cancelFileSave() {
      this.$confirm({
        title: "Cancel batch processing",
        content: "Are you sure to cancel? All uploaded data will be lost",
        okText: "Accept",
        okType: "danger",
        cancelText: "Go Back",
        onOk: () => {
          this.$store.dispatch(
            "adminBatchUserCreateTool/updateFileUploadStatus",
            ""
          );

          // resetting state
          this.file = null;
          this.usersToUpdate = [];
          this.usersToUpdateFinalized = [];
          this.editusersToUpdateId = "";
        },
      });
    },
    getPreviousUpload(current, pageSize) {
      this.previousUpload.loading = true;
      this.$store
        .dispatch("adminBatchUserCreateTool/getUploads", {
          current: current,
          pageSize: pageSize,
          viewAllUploads: false,
        })
        .then((data) => {
          this.previousUpload = {
            ...this.previousUpload,
            loading: false,
            total: data.totalRecords,
            pageSize: pageSize,
            data: data.result,
          };
        });
    },
    getAllUpload(current, pageSize) {
      this.allUpload.loading = true;
      this.$store
        .dispatch("adminBatchUserCreateTool/getUploads", {
          current: current,
          pageSize: pageSize,
          viewAllUploads: true,
        })
        .then((data) => {
          this.allUpload = {
            ...this.allUpload,
            loading: false,
            total: data.totalRecords,
            pageSize: pageSize,
            data: data.result,
          };
        });
    },
    toggleAllUpload() {
      this.allUpload = { ...this.allUpload, visible: !this.allUpload.visible };
    },
    handleChange(value, key, column) {
      const newData = [...this.usersToUpdate];
      const target = newData.filter((item) => key === item.id)[0];
      if (target) {
        target[column] = value;
        this.usersToUpdate = newData;
      }
    },
    edit(key) {
      const newData = [...this.usersToUpdate];
      const target = newData.filter((item) => key === item.id)[0];
      this.editusersToUpdateId = key;
      if (target) {
        target.editable = true;
        this.usersToUpdate = newData;
      }
    },
    save(key) {
      const newData = [...this.usersToUpdate];
      const newCacheData = [...this.usersToUpdateFinalized];
      const target = newData.filter((item) => key === item.id)[0];
      const targetCache = newCacheData.filter((item) => key === item.id)[0];
      if (target && targetCache) {
        delete target.editable;
        this.usersToUpdate = newData;
        Object.assign(targetCache, target);
        this.usersToUpdateFinalized = newCacheData;
      }
      this.editusersToUpdateId = "";
    },
    cancel(key) {
      const newData = [...this.usersToUpdate];
      const target = newData.filter((item) => key === item.id)[0];
      this.editusersToUpdateId = "";
      if (target) {
        Object.assign(
          target,
          this.usersToUpdateFinalized.filter((item) => key === item.id)[0]
        );
        delete target.editable;
        this.usersToUpdate = newData;
      }
    },
    saveUserCreationFile() {
      const { file, uploadResult } = this.lastUploads.at(-1);
      const finalizedUsers = this.usersToUpdateFinalized.filter(
        ({ update }) => update
      );
      const payload = {
        file,
        id: uploadResult.id,
        usersToUpdate: finalizedUsers,
        skipperdUsersCount:
          this.usersToUpdateFinalized.length - finalizedUsers.length,
      };

      // resetting state
      this.usersToUpdate = [];
      this.usersToUpdateFinalized = [];
      this.editusersToUpdateId = "";

      this.$store
        .dispatch("adminBatchUserCreateTool/saveUserCreationFile", payload)
        .then(() => {
          this.getPreviousUpload(1, this.previousUpload.pageSize);
          this.getAllUpload(1, this.allUpload.pageSize);
        });
    },
    closeFileSave() {
      this.$store.dispatch(
        "adminBatchUserCreateTool/updateFileUploadStatus",
        ""
      );
      this.file = null;
    },

    downloadExcel(upload) {
      this.$store
        .dispatch("adminBatchUserCreateTool/downloadExcelFile", {
          ft: "batchusercreatetool",
          id: upload.id,
        })
        .then((data) => {
          saveAs(new Blob([data]), `${upload.file_name}.xls`);
        })
        .catch((err) => {
          alert(err.data.message);
        });
    },
  },

  computed: {
    authorizedClients() {
      return this.$store.getters[
        "adminBatchUserCreateTool/getAuthorizedClients"
      ];
    },
    template() {
      return this.$store.getters["adminBatchUserCreateTool/getTemplate"];
    },
    fileUploadStatus() {
      return this.$store.getters[
        "adminBatchUserCreateTool/getFileUploadStatus"
      ];
    },
    lastUploads() {
      return this.$store.getters["adminBatchUserCreateTool/getLastUploads"];
    },
    fileList() {
      return this.file ? [this.file] : [];
    },
  },

  created() {
    this.setActiveMenuItem();
    this.$store.dispatch("adminBatchUserCreateTool/fetchAuthorizedClients");
    this.$store.dispatch("adminBatchUserCreateTool/getTemplate");
    this.getPreviousUpload(1, this.previousUpload.pageSize);
    this.getAllUpload(1, this.allUpload.pageSize);

    // Reload page confirmation when uploading
    window.addEventListener("beforeunload", (e) => {
      if (
        this.fileUploadStatus === "saving" ||
        this.fileUploadStatus === "uploading" ||
        this.previousUpload.loading ||
        this.allUpload.loading
      ) {
        e.returnValue = "Still Waiting!";
      }
    });
  },

  data() {
    return {
      file: null,
      previousUpload: {
        data: [],
        pageSize: 5,
        loading: false,
      },
      allUpload: {
        data: [],
        pageSize: 10,
        hideOnSinglePage: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        total: 0,
        change: (page, pageSize) => {
          this.getAllUpload(page, pageSize);
        },
        loading: false,
        visible: false,
      },
      ignoredUsersFields: [
        {
          title: "User Name",
          key: "user_name",
          dataIndex: "user_name",
        },
        {
          title: "Email",
          key: "email",
          dataIndex: "email",
        },
        {
          title: "Comment",
          key: "comment",
          dataIndex: "comment",
          ellipsis: true,
        },
      ],
      ignoredUsersPagination: {
        pageSize: 5,
        hideOnSinglePage: true,
      },
      usersToUpdateFields: [
        {
          title: "Update",
          key: "update",
          dataIndex: "update",
          scopedSlots: { customRender: "update" },
          ellipsis: true,
        },
        {
          title: "User Name",
          key: "user_name",
          dataIndex: "user_name",
          scopedSlots: { customRender: "user_name" },
          ellipsis: true,
        },
        {
          title: "Email",
          key: "email",
          dataIndex: "email",
          scopedSlots: { customRender: "email" },
          ellipsis: true,
        },
        {
          title: "First Name",
          key: "first_name",
          dataIndex: "first_name",
          scopedSlots: { customRender: "first_name" },
          ellipsis: true,
        },
        {
          title: "Last Name",
          key: "last_name",
          dataIndex: "last_name",
          scopedSlots: { customRender: "last_name" },
          ellipsis: true,
        },
        {
          title: "Points",
          key: "points",
          dataIndex: "points",
          scopedSlots: { customRender: "points" },
          ellipsis: true,
        },
        {
          title: "Company",
          key: "company",
          dataIndex: "client_id",
          scopedSlots: { customRender: "company" },
          ellipsis: true,
        },
        {
          title: "Locked",
          key: "locked",
          dataIndex: "is_locked",
          scopedSlots: { customRender: "locked" },
          ellipsis: true,
        },
        {
          title: "Edit",
          dataIndex: "operation",
          scopedSlots: { customRender: "operation" },
          ellipsis: true,
        },
      ],
      usersToUpdatePagination: {
        pageSize: 5,
        hideOnSinglePage: true,
      },
      usersToUpdate: [],
      editusersToUpdateId: "",
      usersToUpdateFinalized: [],
      showUploadConfirm: false,
      file: null,
    };
  },
};
</script>
<style lang="scss">
.admin-batch-user-create-tool,
.ant-modal-content {
  .ant-pagination {
    .ant-pagination-next,
    .ant-pagination-total-text {
      display: block;
    }
    .ant-pagination-total-text {
      margin-right: auto;
    }
  }
}
</style>

<style lang="scss" scoped>
.admin-batch-user-create-tool {
  margin: -14px 0 0 0;
  .badge {
    font-size: 0.8rem;
    padding: 6px 0px;
    border-radius: $border-radius-sm;
  }
  a {
    color: #1890ff;
    &:hover {
      color: #40a9ff;
    }
  }
}
.align-right {
  text-align: right;
}

.bold-text {
  font-weight: bold;
}

.display-flex {
  display: flex;
}

.marginAll {
  margin: 10px;
}

.card {
  background-image: url(https://new.incentiveweb.com/img/bg-blue.75d34f7d.svg);
  max-width: 100%;
  border-radius: $border-radius-sm;
  background-position: top center;
  background-repeat: repeat;
  background-size: 90px;
  background-color: #f0f9ff;
}

.summary .custom-control-label {
  line-height: 1.1em;
}
.results-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 0.5%;
  padding-right: 1%;
}
.uploadResult {
  margin-bottom: 2%;
  border-bottom: 0.5px solid #f2f2f2;
  padding-bottom: 1%;
}
.results-row > div {
  flex-basis: 10%;
}
.results-row > div:nth-child(1) {
  flex-basis: 5%;
}
.results-row > div:nth-child(2),
.results-row > div:nth-child(4) {
  flex-basis: 30%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.fileUploadResultAction {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
.lastUploadsHeader {
  display: flex;
  justify-content: space-between;
}
.duplicateUsersContainer {
  margin-top: 5%;
}
.editable-row-operations span {
  display: flex;
  justify-content: space-evenly;
}
</style>
<style>
.ant-upload-list.ant-upload-list-text {
  vertical-align: sub;
  display: inline-block;
}
.ant-pagination {
  display: flex;
  justify-content: flex-end;
  padding-right: 1%;
  margin-top: 5%;
}
.confirm-modal h4 {
  margin-bottom: 10px;
  font-weight: 600;
  font-size: 17px;
}

.confirm-modal p {
  margin-bottom: 6px;
  font-size: 14px;
  color: rgba(0,0,0,0.75);
}

</style>
